import { useEffect } from 'react';
import { Building2, ChevronLeft, Code2, Paintbrush, Users } from 'lucide-react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';

import { api } from '../lib/api';
import { syncAuthAcrossTabs, useAuthStore } from '../stores/auth';
import { useBrandingStore } from '../stores/branding';
import { SettingsViewOnlyBanner } from '../components/settings/SettingsViewOnlyBanner';
import { UserMenu } from '../components/UserMenu';

const SETTINGS_NAV = [
  { to: '/settings/account', label: 'General', title: 'Account profile and preferences', icon: <Building2 size={14} aria-hidden /> },
  { to: '/settings/branding', label: 'Branding & theme', title: 'Organization logo, name, and global theme', icon: <Paintbrush size={14} aria-hidden /> },
  { to: '/settings/team', label: 'User', title: 'Account users and roles', icon: <Users size={14} aria-hidden /> },
  { to: '/settings/projects', label: 'Projects', title: 'Manage projects', icon: <Code2 size={14} aria-hidden /> },
] as const;

export function SettingsLayout(): React.ReactElement | null {
  const token    = useAuthStore((s) => s.token);
  const tenantId = useAuthStore((s) => s.tenantId);
  const role     = useAuthStore((s) => s.role);
  const setRole  = useAuthStore((s) => s.setRole);
  const setBranding = useBrandingStore((s) => s.setBranding);
  const navigate = useNavigate();

  useEffect(() => syncAuthAcrossTabs(), []);

  // Not logged in → login page
  useEffect(() => {
    if (!token) navigate('/login', { replace: true });
  }, [token, navigate]);

  // Branding used by export and appearance pages; the app shell no longer wraps this layout
  useEffect(() => {
    if (!token) return;
    api.get<{
      company_name: string; tagline: string | null;
      palette_key: string; logo_url: string | null;
    }>('/branding').then((b) => {
      setBranding({ paletteKey: b.palette_key, companyName: b.company_name, tagline: b.tagline ?? '', logoUrl: b.logo_url ?? null });
    }).catch(() => { /* best-effort */ });
  }, [token, setBranding]);

  // Role not yet in store (direct navigation to /settings) → fetch it
  useEffect(() => {
    if (!token || !tenantId || role !== null) return;
    api
      .get<Array<{ account_id: string; account_name: string; role: string }>>('/auth/accounts')
      .then((accounts) => {
        const current = accounts.find((a) => a.account_id === tenantId);
        setRole(current?.role ?? null);
      })
      .catch(() => {});
  }, [token, tenantId, role, setRole]);

  // Only bounce unauthenticated users; account users can view settings
  useEffect(() => {
    if (!token) navigate('/login', { replace: true });
  }, [token, navigate]);

  if (!token) return null;

  if (role === null) {
    return (
      <div className="flex h-full min-h-0 w-full flex-1 items-center justify-center bg-surface">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600/30 border-t-brand-600" />
      </div>
    );
  }

  return (
    <SettingsLayoutInner />
  );
}

function SettingsLayoutInner() {
  const navigate  = useNavigate();
  const email     = useAuthStore((s) => s.email) ?? '';
  const isAdmin   = useAuthStore((s) => s.role) === 'admin';
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const handleLogout = () => { clearAuth(); navigate('/login'); };

  return (
    <div className="flex h-full min-h-0 w-full flex-1 overflow-hidden bg-surface">

      {/* Settings sidebar — full height, not under the header */}
      <aside className="flex w-[200px] shrink-0 flex-col border-r border-surface-3 bg-surface-2">
        <div className="flex-1 overflow-y-auto px-3 py-5">
          <p className="mb-2 px-2.5 text-[11px] font-semibold uppercase tracking-wider text-fg-3">
            Settings
          </p>
          <nav className="flex flex-col gap-0.5">
            {SETTINGS_NAV.map(({ to, label, title, icon }) => (
              <NavLink
                key={to}
                to={to}
                title={title}
                className={({ isActive }) =>
                  `flex items-center gap-[11px] rounded-[11px] px-[11px] py-[10px]
                   text-[13.5px] transition-colors duration-150 ${
                    isActive
                      ? 'bg-brand-500/[0.12] text-brand-600 font-semibold'
                      : 'text-fg-2 font-medium hover:bg-surface-3/50 hover:text-fg'
                  }`
                }
              >
                {icon}
                {label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="shrink-0 border-t border-surface-3 px-3 py-3">
          <UserMenu email={email} isAdmin={isAdmin} onLogout={handleLogout} />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 shrink-0 items-center gap-4 border-b border-surface-3 bg-surface px-6">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-[13px] font-medium text-fg-3
                       hover:text-fg transition-colors duration-150"
          >
            <ChevronLeft size={15} />
            Back
          </Link>
          <div className="h-4 w-px bg-surface-3" />
          <span className="text-[14px] font-semibold text-fg">Settings</span>
        </header>

        <main className="flex-1 overflow-y-auto px-8 py-6">
          {!isAdmin && <SettingsViewOnlyBanner />}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
